import React from 'react';
import { inject, observer } from 'mobx-react';
import { reaction, IReactionDisposer } from 'mobx';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps } from '../../../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import TiptapEditor from './components/TiptapEditor';
import VideoPreview from './components/VideoPreview';
import { setPostImage, commonImage } from '@images/imageUrls';
import TopicPicker from '@components/TopicPicker/h5';
import MentionPicker from '@components/MentionPicker/h5';
import EmojiPicker from '@components/EmojiPicker/h5';
import ContentSettings from '@components/ContentSettings/h5';
import { uploadAttachment } from '../../../../server/api';
import Toast from '@components/Toast';
import dayjs from 'dayjs';
import { MAX_THREAD_IMAGES, countNormalImagesFromHtml } from '../../../../utils/setPostLimits';
import { extractMentionsFromHtml, getMentionLimit, mergeMentions } from '../../../../utils/setPostMentions';
import { isSupportedImageFile } from '../../../../utils/imageFile';
import TabNav from '@components/tabNav';

// Toast 封装：若不可用则降级为 alert
const toastSuccess = (msg: string) => {
    try { (Toast as any)?.success ? Toast.success(msg) : alert(msg); } catch { alert(msg); }
};
const toastError = (msg: string) => {
    try { (Toast as any)?.error ? Toast.error(msg) : alert(msg); } catch { alert(msg); }
};

const isScheduleExpired = (fixedAt: string | null | undefined): boolean => {
    if (!fixedAt) return false;
    try {
        const fixedTime = dayjs(fixedAt);
        return fixedTime.isBefore(dayjs());
    } catch (e) {
        return false;
    }
};

/**
 * 获取初始化的发布状态
 * 如果定时时间已过期，重置为立即发布
 */
const getInitialPublishState = (fixedAt: string | null | undefined, publishMode: string) => {
    if (!fixedAt) {
        return { scheduledPublish: false, publishTime: '' };
    }

    if (isScheduleExpired(fixedAt)) {
        // 时间已过期，重置为立即发布
        return { scheduledPublish: false, publishTime: '' };
    }

    // 时间未过期，保持定时发布
    return { 
        scheduledPublish: true, 
        publishTime: fixedAt 
    };
};

interface SetPostH5PageProps extends BasePageProps, RouteComponentProps {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    thread?: ThreadAction;
    setPost?: any;
    setPostData: (data: any) => void;
    onPublishPost: () => void;
    topicList?: any[];
    categoryList?: string[];
    onCategoryChange?: (categoryName: string, page?: number) => void;
    loading?: boolean;
    hasMore?: boolean;
    isLoadingMore?: boolean;
}
interface SetPostH5State {
    loading: boolean;
    error: any;
    pageLoaded: any;
    title: string;
    content: string;
    hasContent: boolean;
    showTopicPicker: boolean;
    showMentionPicker: boolean;
    showEmojiPicker: boolean;
    showContentSettings: boolean;
    aiGenerated: boolean;
    selectedTopics: Array<{ id: number | string; name: string; [key: string]: any }>;
    selectedMentions: Array<{ id: string; name: string; [key: string]: any }>;
    hasDraft: boolean; // 是否有草稿
    restoreDraftLoading: boolean; // 恢复草稿加载中
    saveDraftLoading: boolean; // 存草稿加载中
    scheduledPublish: boolean; // 定时发布开关
    publishTime: string; // 定时发布时间
    navList: any;
    navCurrentIndex: number;
    hasStartedEditing: boolean;
}

@inject('app', 'site', 'user', 'thread', 'setPost')
@observer
class SetPostH5 extends BasePage<SetPostH5PageProps, SetPostH5State> {
    titleDisposer: IReactionDisposer | null = null;
    contentDisposer: IReactionDisposer | null = null;
    topicDisposer: IReactionDisposer | null = null;
    aigcDisposer: IReactionDisposer | null = null;
    imageInputRef = React.createRef<HTMLInputElement>();
    editorRef = React.createRef<any>();
    videoInputRef = React.createRef<HTMLInputElement>();
    setPostStore: any = null;
    state: SetPostH5State = {
        loading: false,
        error: null,
        pageLoaded: false,
        title: '',
        content: '',
        hasContent: false,
        showTopicPicker: false,
        showMentionPicker: false,
        showEmojiPicker: false,
        showContentSettings: false,
        aiGenerated: false,
        selectedTopics: [],
        selectedMentions: [],
        hasDraft: false, // 是否有草稿
        restoreDraftLoading: false, // 恢复草稿加载中
        saveDraftLoading: false, // 存草稿加载中
        scheduledPublish: false, // 定时发布开关
        publishTime: '', // 定时发布时间
        navList: [
            { name: '图文', id: 0, type: '' },
            { name: '问题反馈', id: 1, type: '1' },
        ],
        navCurrentIndex: 0,
        hasStartedEditing: false,
    };

    async componentDidMount() {
        super.componentDidMount();
        const store = this.props.setPost;
        this.setPostStore = store;
        
        const initTitle = store?.postData?.title || '';
        const initContent = store?.postData?.content || '';
        if (initTitle || initContent) {
            this.setState({ title: initTitle, content: initContent, selectedMentions: extractMentionsFromHtml(initContent) });
            this.updateHasContent(initTitle, initContent);
            if (initTitle.trim() || initContent.replace(/<[^>]*>/g, '').trim()) {
                this.setState({ hasStartedEditing: true });
            }
        }
        
        // 初始化定时发布状态，检查时间是否已过期
        const initPublishMode = store?.postData?.publishMode || 'immediate';
        const initFixedAt = store?.postData?.fixedAt;
        const { scheduledPublish, publishTime } = getInitialPublishState(initFixedAt, initPublishMode);
        this.setState({ scheduledPublish, publishTime });
        
        this.titleDisposer = reaction(
            () => store?.postData?.title,
            (newTitle) => {
                const nextTitle = newTitle || '';
                this.setState({ title: nextTitle });
                // 使用新值和当前 state content 更新
                this.updateHasContent(nextTitle, this.state.content);
            }
        );
        
        // 仅追踪 content，直接同步到 state
        this.contentDisposer = reaction(
            () => store?.postData?.content,
            (newContent) => {
                const nextContent = newContent || '';
                this.setState({ content: nextContent, selectedMentions: extractMentionsFromHtml(nextContent) });
                this.updateHasContent(this.state.title, nextContent);
            }
        );

        // 同步话题回显
        this.topicDisposer = reaction(
            () => (store?.postData?.topicIds || []).slice().join(','),
            () => this.syncTopicsFromStore()
        );
        this.syncTopicsFromStore();


        this.aigcDisposer = reaction(
            () => store?.postData?.isAigc,
            (isAigc) => this.setState({ aiGenerated: !!isAigc })
        );

        // 页面初始化时查询是否有草稿
        this.checkDraft();
    }

    componentWillUnmount() {
        if (this.titleDisposer) this.titleDisposer();
        if (this.contentDisposer) this.contentDisposer();
        if (this.topicDisposer) this.topicDisposer();
        if (this.aigcDisposer) this.aigcDisposer();
    }

    componentDidUpdate(prevProps: Readonly<SetPostH5PageProps>) {
        // 话题列表加载完成后，同步选中话题名称
        if ((prevProps.topicList || []).length !== (this.props.topicList || []).length) {
            this.syncTopicsFromStore();
        }
        
        const prevTitle = prevProps.setPost?.postData?.title || '';
        const currTitle = this.props.setPost?.postData?.title || '';
        if (currTitle !== prevTitle && currTitle !== this.state.title) {
            console.log('[H5] componentDidUpdate: title changed', prevTitle, '->', currTitle);
            this.setState({ title: currTitle });
            this.updateHasContent(currTitle, this.state.content);
        }
        
        const prevContent = prevProps.setPost?.postData?.content || '';
        const currContent = this.props.setPost?.postData?.content || '';
        if (currContent !== prevContent && currContent !== this.state.content) {
            console.log('[H5] componentDidUpdate: content changed, length:', currContent.length);
            this.setState({ content: currContent, selectedMentions: extractMentionsFromHtml(currContent) });
            this.updateHasContent(this.state.title, currContent);
        }
        
        // 同步话题
        const prevTopicIds = (prevProps.setPost?.postData?.topicIds || []).join(',');
        const currTopicIds = (this.props.setPost?.postData?.topicIds || []).join(',');
        if (currTopicIds !== prevTopicIds) {
            this.syncTopicsFromStore();
        }
        
        // 同步 AI 声明
        const prevAigc = prevProps.setPost?.postData?.isAigc;
        const currAigc = this.props.setPost?.postData?.isAigc;
        if (currAigc !== prevAigc) {
            this.setState({ aiGenerated: !!currAigc });
        }

        // 同步定时发布设置，检查时间是否已过期
        const prevPublishMode = prevProps.setPost?.postData?.publishMode || 'immediate';
        const currPublishMode = this.props.setPost?.postData?.publishMode || 'immediate';
        const prevFixedAt = prevProps.setPost?.postData?.fixedAt;
        const currFixedAt = this.props.setPost?.postData?.fixedAt;

        if (currPublishMode !== prevPublishMode || currFixedAt !== prevFixedAt) {
            // 使用工具函数检查时间是否过期
            const { scheduledPublish, publishTime } = getInitialPublishState(currFixedAt, currPublishMode);
            this.setState({
                scheduledPublish,
                publishTime,
            });
        }
    }

    syncTopicsFromStore = () => {
        const ids = this.props.setPost?.postData?.topicIds || [];
        const list = this.props.topicList || [];
        const topicCache = this.props.setPost?.topicCache || {};
        console.log('[H5] syncTopicsFromStore - topicIds:', ids, 'topicList length:', list.length, 'cache keys:', Object.keys(topicCache));
        if (!ids.length) {
            this.setState({ selectedTopics: [] });
            return;
        }
        const selected = ids.map((id: any) => {
            const idStr = String(id);
            
            // 优先从缓存中查找（编辑模式下，话题信息通常已在缓存中）
            if (topicCache[idStr]) {
                console.log('[H5] matched topic from cache:', topicCache[idStr]);
                return topicCache[idStr];
            }
            
            // 再从列表中查找
            const matched = list.find((t: any) => String(t.id) === idStr);
            if (matched) {
                console.log('[H5] matched topic from list:', matched);
                return matched;
            }
            
            // 如果话题列表还未加载，暂时使用更好的占位符
            console.log('[H5] topic not found in list or cache, using placeholder for id:', id);
            return { 
                id: idStr, 
                name: `话题 ${id}`, 
                cover: commonImage.official2,
                count: '加载中...' 
            };
        });
        console.log('[H5] setting selectedTopics:', selected);
        this.setState({ selectedTopics: selected });
    }

    handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const title = e.target.value;
        this.setState((prevState) => ({
            title,
            hasStartedEditing: prevState.hasStartedEditing || title.trim() !== '',
        }));
        const trimmedTitle = title.trim();
        const trimmedContent = this.state.content.replace(/<[^>]*>/g, '').trim();
        const hasContent = trimmedTitle !== '' || trimmedContent !== '';
        this.setState({ hasContent });
        this.props.setPostData({ title });
    }

    handleContentChange = (content: string) => {
        const parsedMentions = extractMentionsFromHtml(content);
        this.setState((prevState) => ({
            content,
            selectedMentions: parsedMentions,
            hasStartedEditing: prevState.hasStartedEditing || (content || '').replace(/<[^>]*>/g, '').trim() !== '',
        }));
        const trimmedContent = (content || '').replace(/<[^>]*>/g, '').trim();
        const trimmedTitle = this.state.title.trim();
        const hasContent = trimmedTitle !== '' || trimmedContent !== '';
        this.setState({ hasContent });
        this.props.setPostData({ content, mentions: parsedMentions });
    }

    navClick = (_item: any, index: number) => {
        this.setState({ navCurrentIndex: index }, () => {
            if (index === 1) {
                this.props.history.push('/setFeedbackPost');
            }
        });
    }

    updateHasContent = (title: string, content: string) => {
        // 需要移除 HTML 标签后判断是否真的有文本内容
        const cleanContent = (content || '').replace(/<[^>]*>/g, '').trim();
        const hasContent = title.trim() !== '' || cleanContent !== '';
        this.setState({ hasContent });
    }

    handleSaveDraft = () => {
        if (!this.state.hasContent) return;
        // 手动保存草稿
        this.handleSaveDraftManually();
    }

    handlePreview = () => {
        if (!this.state.hasContent) return;
        // TODO: 预览逻辑
        console.log('预览');
    }

    /**
     * 查询是否有草稿
     */
    checkDraft = async () => {
        const { setPost } = this.props;
        if (!setPost?.checkDraft) return;

        const ret = await setPost.checkDraft();
        if (ret.code === 0 && ret.data?.hasDraft) {
            this.setState({ hasDraft: true });
        }
    }

    /**
     * 恢复草稿
     */
    handleRestoreDraft = async () => {
        const { setPost } = this.props;
        if (!setPost?.restoreDraft || !setPost?.draftData?.draftThreadId) {
            toastError('无法恢复草稿');
            return;
        }

        this.setState({ restoreDraftLoading: true });
        try {
            const ret = await setPost.restoreDraft(setPost.draftData.draftThreadId);
            if (ret.code === 0) {
                toastSuccess('草稿已恢复');
                this.setState({ hasDraft: false });

                // 同步定时发布状态，检查时间是否已过期
                const storePublishMode = setPost.postData?.publishMode || 'immediate';
                const storeFixedAt = setPost.postData?.fixedAt;
                const { scheduledPublish, publishTime } = getInitialPublishState(storeFixedAt, storePublishMode);
                this.setState({
                    scheduledPublish,
                    publishTime,
                });
            } else {
                toastError(ret.message || '恢复草稿失败');
            }
        } catch (error) {
            toastError('恢复草稿出错');
        } finally {
            this.setState({ restoreDraftLoading: false });
        }
    }

    /**
     * 手动保存草稿
     */
    handleSaveDraftManually = async () => {
        const { setPost } = this.props;
        if (!setPost?.saveDraftManually) {
            toastError('缺少保存方法');
            return;
        }

        // 需要移除 HTML 标签后判断是否真的有文本内容
        const cleanContent = (this.state.content || '').replace(/<[^>]*>/g, '').trim();
        if (!cleanContent) {
            toastError('请输入内容');
            return;
        }

        this.setState({ saveDraftLoading: true });
        try {
            // 同步定时发布设置到 store，确保草稿保存时包含定时发布时间
            if (this.state.scheduledPublish && this.state.publishTime) {
                setPost.setPostData({
                    publishMode: 'scheduled',
                    fixedAt: this.state.publishTime,
                });
            } else {
                setPost.setPostData({
                    publishMode: 'immediate',
                    fixedAt: null,
                });
            }
            
            const ret = await setPost.saveDraftManually(null, null);
            if (ret.code === 0) {
                toastSuccess('草稿已保存');
            } else {
                toastError(ret.message || '保存草稿失败');
            }
        } catch (error) {
            toastError('保存草稿出错');
        } finally {
            this.setState({ saveDraftLoading: false });
        }
    }

    handlePublish = async () => {
        if (!this.props.setPost?.addLog) return;
        try {
            this.props.setPost.addLog('ui_publish_click', {
                titleLength: (this.state.title || '').length,
                contentLength: (this.state.content || '').length,
                topicCount: this.state.selectedTopics?.length || 0,
                mentionCount: this.state.selectedMentions?.length || 0,
                aiGenerated: !!this.state.aiGenerated,
                hasContent: !!this.state.hasContent,
                scheduledPublish: !!this.state.scheduledPublish,
                publishTime: this.state.publishTime || '',
            });
        } catch (_) {}
        
        // 发布前同步定时发布设置到 store
        if (this.state.scheduledPublish && this.state.publishTime) {
            this.props.setPostData({
                publishMode: 'scheduled',
                fixedAt: this.state.publishTime,
            });
        } else {
            this.props.setPostData({
                publishMode: 'immediate',
                fixedAt: null,
            });
        }
        
        this.props.onPublishPost();
    }

    handleTopicClick = () => {
        this.setState({ showTopicPicker: true });
    }

    handleTopicSelect = (topics: any[]) => {
        console.log('[H5] handleTopicSelect called with topics:', topics);
        // H5 端 TopicPicker 返回的已是最终选择
        this.setState({ selectedTopics: topics });
        const topicIds = (topics || []).map(t => t.id);
        console.log('[H5] setting topicIds:', topicIds);
        try { this.props.setPost?.addLog('ui_topic_select', { count: topicIds.length, topicIds }); } catch (_) {}
        this.props.setPostData({ topicIds });
    }

    handleTopicClose = () => {
        this.setState({ showTopicPicker: false });
    }

    // 删除话题
    handleRemoveTopic = (index: number) => {
        const newTopics = this.state.selectedTopics.filter((_, idx) => idx !== index);
        this.setState({ selectedTopics: newTopics });
        const topicIds = (newTopics || []).map(t => t.id);
        try { this.props.setPost?.addLog('ui_topic_remove', { index, topicIds }); } catch (_) {}
        this.props.setPostData({ topicIds });
    }

    // @好友弹窗
    handleMentionClick = () => {
        this.setState({ showMentionPicker: true });
    }

    handleMentionSelect = (mentions: any[]) => {
        if (!mentions || mentions.length === 0) {
            return true;
        }

        const currentMentions = extractMentionsFromHtml(this.state.content || this.props.setPost?.postData?.content || '');
        const mergedMentions = mergeMentions(currentMentions, mentions || []);
        const limit = getMentionLimit(this.props.user?.userInfo);

        if (Number.isFinite(limit) && mergedMentions.length > limit) {
            toastError(`为维护友好的交流氛围，单条内容最多支持@${limit}位好友`);
            return false;
        }

        const mentionHtml = mentions.map(m => `<span class="member" contenteditable="false" data-code="${m.id}">@${m.name}</span>`).join(' ');
        const baseContent = this.state.content || this.props.setPost?.postData?.content || '';
        const newContent = `${baseContent}${baseContent ? ' ' : ''}${mentionHtml}<p></p>`;
        const parsedMentions = extractMentionsFromHtml(newContent);

        this.setState({ content: newContent, selectedMentions: parsedMentions });
        this.props.setPostData({ content: newContent, mentions: parsedMentions });
        return true;
    }

    handleMentionClose = () => {
        this.setState({ showMentionPicker: false });
    }

    // 删除@好友
    handleRemoveMention = (index: number) => {
        const newMentions = this.state.selectedMentions.filter((_, idx) => idx !== index);
        this.setState({ selectedMentions: newMentions });
        this.props.setPostData({ mentions: newMentions });
    }

    // Emoji 表情弹窗
    handleEmojiClick = () => {
        this.setState({ showEmojiPicker: true });
    }

    handleEmojiSelect = (emoji: string, emojiCode?: string, meta?: { category?: string }) => {
        if (emojiCode === ':delete:') {
            this.handleEmojiDelete();
            return;
        }

        const editor = this.editorRef.current;
        if (editor) {
            const isImageEmoji = typeof emoji === 'string' && emoji.startsWith('http');
            const isDefaultCategory = meta?.category === '默认';
            const defaultEmojiSize = 28;
            if (isImageEmoji) {
                const size = isDefaultCategory ? defaultEmojiSize : 59;
                const attrs = isDefaultCategory
                    ? ({ alt:emojiCode,src: emoji, 'data-emoji': 'default', width: size, height: size, style: `display:inline-block;vertical-align:middle;` } as any)
                    : ({ alt:emojiCode,src: emoji, 'data-emoji': 'large', width: size, height: size, style: `display:inline-block;vertical-align:middle;` } as any);

                const ok = editor.chain().focus().setImage(attrs).insertContent({ type: 'text', text: ' ' }).run();
                if (!ok) {
                    editor.chain().focus()
                        .insertContent(`<img src="${emoji}" alt="emoji" data-emoji="${isDefaultCategory ? 'default' : 'large'}" style="width: ${size}px; height: ${size}px; display: inline-block; vertical-align: middle;" />`)
                        .insertContent({ type: 'text', text: ' ' })
                        .run();
                }
            } else {
                editor.chain().focus().insertContent(emoji).insertContent({ type: 'text', text: ' ' }).run();
            }
            try { this.props.setPost?.addLog('ui_emoji_insert', { emoji, emojiCode, category: meta?.category }); } catch (_) {}
            return;
        }

        // 兜底：无编辑器实例时，在末尾追加
        let emojiHtml = '';
        if (emoji && emoji.startsWith && emoji.startsWith('http')) {
            const isDefault = meta?.category === '默认';
            const size = isDefault ? '28px' : '59px';
            emojiHtml = `<img src="${emoji}" class="emoji-img" style="width: ${size} !important; height: ${size} !important; vertical-align: middle;" alt="${emojiCode || 'emoji'}" />`;
        } else {
            emojiHtml = `<span class="emoji-text">${emoji}</span>`;
        }
        const newContent = (this.state.content || '') + emojiHtml + '&nbsp;';
        this.setState({ content: newContent });
        this.props.setPostData({ content: newContent });
    }

    handleEmojiClose = () => {
        this.setState({ showEmojiPicker: false });
    }

    // 内容设置弹窗
    handleContentSettingsClick = () => {
        this.setState({ showContentSettings: true });
    }

    handleContentSettingsClose = () => {
        this.setState({ showContentSettings: false });
    }

    handleContentSettingsConfirm = (settings: { aiGenerated: boolean; hasCopyright: boolean; scheduledPublish: boolean; publishTime?: string }) => {
        this.setState({ 
            aiGenerated: settings.aiGenerated,
            scheduledPublish: settings.scheduledPublish,
            publishTime: settings.publishTime || ''
        });
        // 同时同步到 store，使用 publishMode 和 fixedAt 字段名
        const publishMode = settings.scheduledPublish ? 'scheduled' : 'immediate';
        const fixedAt = settings.scheduledPublish && settings.publishTime ? settings.publishTime : null;
        this.props.setPostData({ 
            isAigc: settings.aiGenerated,
            hasCopyright: settings.hasCopyright,
            publishMode: publishMode,
            fixedAt: fixedAt,
        });
    }

    // 删除编辑器内容
    handleEmojiDelete = () => {
        const editor = this.editorRef.current;
        if (!editor) return;
        // 先确保编辑器聚焦
        editor.chain().focus().run();
        const { state, view } = editor;
        const { from, empty, $from } = state.selection as any;
        // 有选区则直接删除选区
        if (!empty) {
            editor.commands.deleteSelection();
            return;
        }
        if (from > 0) {
            const textBeforeCursor = $from.parent?.textContent?.slice(0, $from.parentOffset) || '';
            const lastChar = (Array.from(textBeforeCursor).pop() as string | undefined) || '';
            const deleteLength = lastChar.length || 1;
            const tr = state.tr.delete(Math.max(from - deleteLength, 0), from);
            view.dispatch(tr);
        }
    }

    // 处理图片上传
    handleImageClick = () => {
        this.imageInputRef.current?.click();
    }

    // 触发视频选择
    handleVideoClick = () => {
        // 检查是否已有视频
        if (this.props.setPost?.postData?.video) {
            Toast.info({ content: '只可添加1个视频' });
            return;
        }
        this.videoInputRef.current?.click();
    }

    // 处理图片文件选择
    handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const currentImageCount = countNormalImagesFromHtml(this.props.setPost?.postData?.content || this.state.content || '');
        const remainingSlots = MAX_THREAD_IMAGES - currentImageCount;
        if (remainingSlots <= 0) {
            toastError(`图文内容最多上传 ${MAX_THREAD_IMAGES} 张图片`);
            e.target.value = '';
            return;
        }

        const selectedFiles = Array.from(files);
        const filesToUpload = selectedFiles.slice(0, remainingSlots);
        if (filesToUpload.length < selectedFiles.length) {
            toastError(`图文内容最多上传 ${MAX_THREAD_IMAGES} 张图片，超出的图片未添加`);
        }

        try { this.props.setPost?.addLog('ui_image_upload:start', { count: files.length, currentImageCount, acceptedCount: filesToUpload.length }); } catch (_) {}
        for (const file of filesToUpload) {
            try {
                // 验证文件类型
                if (!isSupportedImageFile(file)) {
                    toastError('请选择图片文件');
                    try { this.props.setPost?.addLog('ui_image_upload:invalid_type', { name: file.name, type: file.type }); } catch (_) {}
                    continue;
                }

                // 验证文件大小 (限制 50MB)
                if (file.size > 50 * 1024 * 1024) {
                    toastError('图片大小不能超过 50MB');
                    try { this.props.setPost?.addLog('ui_image_upload:oversize', { name: file.name, size: file.size }); } catch (_) {}
                    continue;
                }

                // 创建 FormData 并上传
                const formData = new FormData();
                formData.append('type', '1'); // 1 = 图片
                formData.append('file', file);

                const closeLoading = Toast.loading({
                    content: '图片上传中，请稍候...',
                    duration: 0,
                });
                const response = await uploadAttachment(formData).finally(closeLoading);
                const imageData = response.data as { url?: string; webpUrl?: string } | undefined;
                const imageUrl = imageData?.webpUrl || imageData?.url;

                if (response.code === 0 && imageUrl) {
                    // 上传成功，更新content
                    const imageHtml = `<img src="${imageUrl}" style="width: 260px; height: auto;border-radius: 8px;" alt="上传的图片" />`;
                    const newContent = this.state.content + '<p></p>' + imageHtml + '<p></p>';
                    this.setState({ content: newContent });
                    this.props.setPostData({ content: newContent });
                    try { this.props.setPost?.addLog('ui_image_upload:success', { url: imageUrl, name: file.name, size: file.size }); } catch (_) {}
                    const updatedImageCount = countNormalImagesFromHtml(newContent);
                    if (updatedImageCount >= MAX_THREAD_IMAGES) {
                        toastError(`已达到图文图片上限 ${MAX_THREAD_IMAGES} 张`);
                    }
                } else {
                    try { this.props.setPost?.addLog('ui_image_upload:api_failed', { code: response.code, message: response.message }); } catch (_) {}
                    toastError(response.message || '图片上传失败');
                }
            } catch (error: any) {
                console.error('图片上传失败:', error);
                try { this.props.setPost?.addLog('ui_image_upload:error', { message: error?.message }); } catch (_) {}
                toastError('图片上传失败: ' + (error.message || '未知错误'));
            }
        }

        // 清空输入框，允许重复选择
        e.target.value = '';
    }

    render() {
        const { hasContent, navList, navCurrentIndex, hasStartedEditing } = this.state;
        // 优先使用 store 中的数据，确保编辑模式回显
        const displayTitle = this.props.setPost?.postData?.title ?? this.state.title ?? '';
        const displayContent = this.props.setPost?.postData?.content ?? this.state.content ?? '';
        const trimmedTitle = (displayTitle || '').trim();
        const trimmedContent = (displayContent || '').replace(/<[^>]*>/g, '').trim();
        const computedHasContent = trimmedTitle !== '' || trimmedContent !== '';

        return (
            <div className={styles.wrapper}>
                {!hasStartedEditing && (
                    <div className={styles.navListBox}>
                        <TabNav
                            navList={navList}
                            currentIndex={navCurrentIndex}
                            navClick={(item: any, index: number) => this.navClick(item, index)}
                        />
                    </div>
                )}
                {/* 标题输入区（仅 type=0 显示） */}
                {Number(this.props.setPost?.postData?.type) === 0 && (
                    <div className={styles.titleBox}>
                        <input
                            className={styles.titleInput}
                            placeholder="添加标题(30字以内)"
                            type="text"
                            maxLength={30}
                            value={displayTitle}
                            onChange={this.handleTitleChange}
                        />
                    </div>
                )}

                {/* 富文本编辑器 */}
                <div className={styles.editorBox}>
                    <TiptapEditor
                        content={displayContent}
                        onContentChange={this.handleContentChange}
                        imageInputRef={this.imageInputRef}
                        editorRef={this.editorRef}
                        videoInputRef={this.videoInputRef}
                        onVideoUpload={this.handleVideoUploaded}
                        hasVideo={!!this.props.setPost?.postData?.video}
                    />
                </div>

                {/* 话题标签显示区 */}
                <div className={styles.topicListWrap}>
                    {this.state.selectedTopics.map((topic, idx) => (
                        <div className={styles.topicTag} key={topic.id}>
                            <img className={styles.topicIcon} src={setPostImage.topicActive} alt="#" />
                            <span className={styles.topicText}>{topic.name}</span>
                            <img className={styles.topicClose} src={setPostImage.deleteTopic} alt="删除" onClick={() => this.handleRemoveTopic(idx)} />
                        </div>
                    ))}
                </div>

                {/* @好友标签显示区 */}
                {/* <div className={styles.mentionListWrap}>
                    {this.state.selectedMentions.map((mention, idx) => (
                        <div className={styles.mentionTag} key={mention.id}>
                            <span className={styles.mentionText}>@{mention.name}</span>
                            <span className={styles.mentionClose} onClick={() => this.handleRemoveMention(idx)}>×</span>
                        </div>
                    ))}
                </div> */}

                {/* 工具栏 */}
                <div className={styles.toolbarBox}>
                    <div className={styles.toolbarLeft}>
                        <div className={styles.toolItem} onClick={this.handleTopicClick}>
                            <img
                                src={(this.state.showTopicPicker || this.state.selectedTopics.length > 0)
                                    ? setPostImage.topicActive2
                                    : setPostImage.topicH5}
                                alt="#"
                                className={styles.toolIcon}
                            />
                        </div>
                        {this.props.site?.otherPermissions?.canInsertThreadImage && (
                            <div className={styles.toolItem} onClick={this.handleImageClick}>
                                <img src={setPostImage.photo} alt="图片" className={styles.toolIcon} />
                            </div>
                        )}
                        {this.props.site?.otherPermissions?.canInsertThreadVideo && (
                            <div className={styles.toolItem} onClick={this.handleVideoClick}>
                                <img src={this.props.setPost?.postData?.video ? setPostImage.videoGray : setPostImage.video} alt="视频" className={styles.toolIcon} />
                            </div>
                        )}
                        <div className={styles.toolItem} onClick={this.handleMentionClick}>
                            <img src={setPostImage.at} alt="@" className={styles.toolIcon} />
                        </div>
                        <div className={styles.toolItem} onClick={this.handleEmojiClick}>
                            <img src={setPostImage.emoji} alt="表情" className={styles.toolIcon} />
                        </div>
                        <div className={styles.toolItem} onClick={this.handleContentSettingsClick}>
                            <img src={this.props.setPost?.postData?.isAigc ? setPostImage.contentSettingActive : setPostImage.setting} alt="设置" className={styles.toolIcon} />
                        </div>
                    </div>
                </div>

                {/* 隐藏的图片上传input */}
                <input
                    ref={this.imageInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={this.handleImageUpload}
                />

                {/* 视频预览（编辑器下方，行为与 PC 统一：依据 postData.video 展示） */}
                {this.props.setPost?.postData?.video ? (
                    <div className={styles.videoPreview}>
                        <VideoPreview
                            key={this.props.setPost.postData.video}
                            src={this.props.setPost.postData.video}
                            suspended={this.state.showTopicPicker || this.state.showMentionPicker || this.state.showEmojiPicker || this.state.showContentSettings}
                        />
                        <img
                            src={setPostImage.delete}
                            alt="删除视频"
                            className={styles.videoDeleteIcon}
                            onClick={this.handleVideoRemove}
                        />
                    </div>
                ) : null}

                {/* 话题选择器 */}
                <TopicPicker
                    visible={this.state.showTopicPicker}
                    onClose={this.handleTopicClose}
                    onSelect={this.handleTopicSelect}
                    topicList={this.props.topicList || []}
                    categoryList={this.props.categoryList || []}
                    onCategoryChange={this.props.onCategoryChange}
                    selectedTopicIds={this.props.setPost?.postData?.topicIds || []}
                    topicCache={this.props.setPost?.topicCache || {}}
                    initialSelected={(() => {
                        const topicIds = this.props.setPost?.postData?.topicIds || [];
                        const topicList = this.props.topicList || [];
                        return topicIds.map(id => topicList.find(topic => String(topic.id) === String(id))).filter(Boolean);
                    })()}
                    hasMore={this.props.hasMore}
                    isLoadingMore={this.props.isLoadingMore}
                />

                {/* @好友选择器 */}
                <MentionPicker
                    visible={this.state.showMentionPicker}
                    onClose={this.handleMentionClose}
                    onSelect={this.handleMentionSelect}
                />

                {/* Emoji表情选择器 */}
                <EmojiPicker
                    visible={this.state.showEmojiPicker}
                    onClose={this.handleEmojiClose}
                    onSelect={this.handleEmojiSelect}
                    onDelete={this.handleEmojiDelete}
                    showEmojiTab
                />

                {/* 内容设置弹窗 */}
                <ContentSettings
                    visible={this.state.showContentSettings}
                    onClose={this.handleContentSettingsClose}
                    onConfirm={this.handleContentSettingsConfirm}
                    initialSettings={{ 
                        aiGenerated: this.props.setPost?.postData?.isAigc ?? false,
                        hasCopyright: this.props.setPost?.postData?.hasCopyright ?? false,
                        scheduledPublish: this.state.scheduledPublish,
                        publishTime: this.state.publishTime
                    }}
                    canModifySchedule={(() => {
                        // 判断是否可以修改发布模式
                        // 定时发布的帖子（有fixedAt且时间未过期）可以修改
                        // 只有已正式发布的帖子（非定时或定时已过且已发布）才禁用
                        const hasId = !!this.props.setPost?.postData?.id;
                        const isDraft = !!this.props.setPost?.postData?.isDraft;
                        const currentFixedAt = this.props.setPost?.postData?.fixedAt;
                        
                        // 判断当前定时时间是否未过期（即帖子还在等待定时发布）
                        const isWaitingScheduled = currentFixedAt && !isScheduleExpired(currentFixedAt);
                        
                        // 可以修改的情况：
                        // 1. 新建帖子（无ID）
                        // 2. 草稿
                        // 3. 定时发布但时间未到的帖子（可以改成立即发布或修改定时时间）
                        return !hasId || isDraft || isWaitingScheduled;
                    })()}
                />

                <div className={styles.fillBetween} />

                {/* 底部操作区 */}
                <div className={styles.footerBox}>
                    {(() => {
                        const isRestoredDraft = !!this.props.setPost?.draftData?.isRestoreDraft || !!this.props.setPost?.postData?.isDraft;
                        const isEditingPublishedPost = !!this.props.setPost?.postData?.id && !isRestoredDraft;
                        return (
                            <>
                                {/* 仅正式编辑态隐藏草稿按钮；恢复草稿后仍可继续保存草稿 */}
                                {!isEditingPublishedPost && (
                                    <button 
                                        className={`${styles.draftSaveBtn}`}
                                        onClick={this.handleSaveDraftManually}
                                        disabled={!this.state.hasContent || this.state.saveDraftLoading}
                                    >
                                        <img 
                                            src={this.state.saveDraftLoading ? setPostImage.draft : setPostImage.draftActive} 
                                            alt="" 
                                            className={styles.draftIcon} 
                                        />
                                        <span>{this.state.saveDraftLoading ? '保存中...' : '存草稿'}</span>
                                    </button>
                                )}
                                {!isEditingPublishedPost && (
                                    <button 
                                        className={`${styles.restoreDraftBtn}`}
                                        onClick={this.handleRestoreDraft}
                                        disabled={!this.state.hasDraft || this.state.restoreDraftLoading}
                                    >
                                        <span>{this.state.restoreDraftLoading ? '恢复中...' : '恢复草稿'}</span>
                                    </button>
                                )}
                                <button 
                                    className={`${styles.publishBtn} ${computedHasContent ? styles.active : ''} ${isEditingPublishedPost ? styles.publishBtnFull : ''}`}
                                    onClick={this.handlePublish}
                                    disabled={!computedHasContent}
                                >
                                    {this.state.scheduledPublish && this.state.publishTime ? '定时发布' : '发布'}
                                </button>
                            </>
                        );
                    })()}
                </div>
            </div>
        );
    }

    // 视频上传成功后的回调
    handleVideoUploaded = ({ url, id, file, videoCoverUrl }: { url: string; id?: number; file?: File; videoCoverUrl?: string }) => {
        try { this.props.setPost?.addLog('ui_video_upload:success', { url, id, size: file?.size }); } catch (_) {}
        const sp = this.props.setPost;
        
        if (sp && sp.setVideo) {
            sp.setVideo(url);
        }

        // 保存 videoId
        if (id && sp) {
            sp.setPostData({
                videoId: id,
            });
        }

        // 保存视频自身的封面信息
        if (videoCoverUrl && sp) {
            sp.setPostData({
                videoCoverUrl: videoCoverUrl,
            });
        }

        // 强制触发状态更新，确保视频实时回显
        this.forceUpdate();
    }

    handleVideoRemove = () => {
        const currentUrl = this.props.setPost?.postData?.video;
        try { this.props.setPost?.addLog('ui_video_remove', { url: currentUrl }); } catch (_) {}
        const sp = this.props.setPost;
        if (sp && sp.setVideo) {
            sp.setVideo('');
            // 同时清空 videoId 和相关视频信息
            sp.setPostData({
                videoId: 0,
                videoCoverUrl: '',
            });
            // 强制触发状态更新，确保视频删除立即生效
            this.forceUpdate();
        }
    }
}

export default withRouter(SetPostH5);
